import * as Sentry from '@sentry/react';

const normalizeBrand = (brand = '') =>
    brand.toLowerCase().replace(/[\s;()/_-]+/g, '');

const isGreaseBrand = (brand = '') => {
    const b = normalizeBrand(brand);
    return b.includes('not') && b.includes('brand');
};

const applyDeviceInfoTags = (ch, nav, ua) => {
    // -------- OS normalize --------
    const osFromCH = (ch?.platform || '').toLowerCase(); // 'windows','macos','android','ios','linux','chrome os'
    // iPhone/iPad UAs contain "like Mac OS X", so the iOS test has to run
    // before the macOS test. `ios` also matches CriOS, FxiOS, and EdgiOS.
    let os =
        osFromCH ||
        (/windows nt|win32/.test(ua)
            ? 'windows'
            : /iphone|ipad|ipod|ios/.test(ua)
              ? 'ios'
              : /mac os x|macintosh/.test(ua)
                ? 'macos'
                : /android/.test(ua)
                  ? 'android'
                  : /cros/.test(ua)
                    ? 'chromeos'
                    : /linux/.test(ua)
                      ? 'linux'
                      : 'other');

    // iPadOS desktop mode: Macintosh UA, no iPad token.
    if (
        os === 'macos' &&
        ((/macintosh/.test(ua) && /mobile/.test(ua)) ||
            (nav.platform === 'MacIntel' && nav.maxTouchPoints > 1))
    ) {
        os = 'ios';
    }

    // -------- Browser normalize (Chromium grease-safe) --------
    let browser;

    if (nav.userAgentData?.brands?.length) {
        const rawBrands = nav.userAgentData.brands.map((b) => b.brand);

        const realBrands = rawBrands.filter((b) => !isGreaseBrand(b));

        // Case 1: only greased brands → Chromium-based browser
        if (realBrands.length === 0) {
            browser = 'chromium';
        } else {
            const brands = realBrands.map(normalizeBrand);

            if (brands.some((b) => b.includes('samsung'))) browser = 'samsung';
            else if (brands.some((b) => b.includes('edge'))) browser = 'edge';
            else if (brands.some((b) => b.includes('opera') || b === 'op'))
                browser = 'opera';
            else if (brands.some((b) => b.includes('firefox')))
                browser = 'firefox';
            else if (brands.some((b) => b.includes('safari')))
                browser = 'safari';
            else if (brands.some((b) => b.includes('chrome')))
                browser = 'chrome';
            else browser = 'chromium';
        }
    } else {
        // -------- UA fallback --------
        browser = /samsungbrowser\/\d+/.test(ua)
            ? 'samsung'
            : /edg\//.test(ua)
              ? 'edge'
              : /opr\/|opera/.test(ua)
                ? 'opera'
                : /firefox\//.test(ua)
                  ? 'firefox'
                  : /safari\//.test(ua) && !/chrome\//.test(ua)
                    ? 'safari'
                    : /chrome\//.test(ua)
                      ? 'chrome'
                      : 'other';
    }

    // -------- Device type normalize --------
    let device = /iphone|ipod|android.+mobile|windows phone/.test(ua)
        ? 'mobile'
        : /ipad|android(?!.*mobile)|tablet/.test(ua)
          ? 'tablet'
          : 'desktop';

    // iPadOS fix again (desktop-class UA)
    if (device === 'desktop' && os === 'ios') device = 'tablet';

    Sentry.setTags({
        os, // 'windows'|'macos'|'android'|'ios'|'linux'|'chromeos'|'other'
        browser, // 'samsung'|'chrome'|'edge'|'safari'|'firefox'|'opera'|'other'
        device, // 'mobile'|'tablet'|'desktop'
    });
};

/**
 * Sets low-cardinality Sentry tags for os/browser/device.
 *
 * Intentionally avoids async/await: Babel transpiles async functions to
 * `Promise.resolve(...).then(...)`, which throws in environments where the
 * global Promise object is broken (common for bots/crawlers) and cascades into
 * Sentry's own sendEnvelope handler.
 */
export const setDeviceInfoTags = () => {
    try {
        const nav = window.navigator || {};
        const ua = (nav.userAgent || '').toLowerCase();

        const setTagsFromClientHints = (ch) => {
            try {
                applyDeviceInfoTags(ch, nav, ua);
            } catch {
                console.error('can not set device info');
            }
        };

        const getHighEntropyValues = nav.userAgentData?.getHighEntropyValues;

        if (typeof getHighEntropyValues === 'function') {
            try {
                const clientHintsPromise = getHighEntropyValues.call(
                    nav.userAgentData,
                    [
                        'platform',
                        'platformVersion',
                        'model',
                        'uaFullVersion',
                        'architecture',
                    ]
                );

                if (
                    clientHintsPromise &&
                    typeof clientHintsPromise.then === 'function'
                ) {
                    clientHintsPromise
                        .then(setTagsFromClientHints)
                        .catch(() => setTagsFromClientHints(null));
                    return;
                }
            } catch {
                console.error('no userAgentData');
            }
        }

        setTagsFromClientHints(null);
    } catch {
        console.error('can not set device info');
    }
};
